import {Keys} from '@blueprintjs/core'
import {TabNode} from '@flexlayout'
import {CatalogLibrariesStore, GroupByGroupStore} from '@modules/catalog'
import {CatalogItemSidebarDetailsStore, DetailsNavItemStore} from '@modules'
import {nav, IDragDropElement, DragDropInteraction, ICatalogFieldConfig, LibraryItemConfig, SidebarVariant, deployments, ui, site, OfflineItemWrapper, WorkspaceConfig, CatalogStore, Workspace} from '@stores'
import {CatalogInstanceChildStore} from '@stores/catalog/CatalogInstanceChildStore'
import {FlexDetailsLocation} from '@stores/catalog/flexLayout'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {removeUndefinedsNested, authStore} from '@utils'
import {Base64} from 'js-base64'
import {action, autorun, computed, observable, runInAction, toJS, when} from 'mobx'
import {React, reaction, _, jsonComputedOptions, SplitterLayout, stringify, observe, useAsync} from '@api'
import {IDisposer} from 'mobx-utils'
import * as css from './SidebarStore.css'

export const DEBUG_SIDEBAR = false

/**
 * From where is the current sidebar action being taken?
 *
 * For example, the user can group by Merch Product, hover over a group, and then open that Product in the left sidebar
 * Or, they could double click a catalog item to open it in the right sidebar
 * Or they could browse the left libraries and drill down to a library or specific item
 * Or drill down on the right into a specific colorway
 * etc...
 */
export type ISidebarContext = 'catalog' // Clicking on a group header
  | 'xref' // Clicking on a catalog item's subfield that cross-references to another ES index with more data
  | 'libraries' // From browsing the left sidebar, can be seasonally contextual,
  | 'create-catalog-item'
  | 'navItem'
  | string

export interface ISidebarWhereClause {
  field?: string,
  value: any
}

export interface SidebarQueryParams {
  open?: boolean,
  width?: number,
  searchText?: string,
  tab?: number | string,
  path?: Array<ICatalogItemDetailsPathElement>,
  libraryView?: 'card' | 'list'
  showImageSlider?: boolean,
  autoFocusDetailItem?: boolean
}

export interface ICatalogItemDetailsPathElement {
  /**
   * When a item is in a group it has a path of groupId.subGroupId.catalogItemId
   */
  idWithPath?: string

  libraryView?: 'card' | 'list'

  /**
   * If not set, assume the selectedItemId is from the catalog index
   */
  libraryId?: string

  /**
   * The library may have multiple images and the user double clicked one of them in the card view so we need to select it in the sidebar
   */
  imageId?: string

  esIndex?: string

  /**
   * Instead of a library, we can be using the sidebar to assign attributes
   */
  catalogField?: string

  /**
   * Helpful when switching between details that haven't been loaded from nav when you already know the target label
   */
  label?: string
  /**
   * Whatever thing is being shown in the sidebar, this may need to change to support drilldown or we can use . notation
   * `.catalogItemId.styleId.colorwayId` for example if drilled down to a colorway from a detail sidebar
   */
  //libraryItemId?: string
  where?: Array<ISidebarWhereClause>
  searchText?: string
  sidebarContext?: ISidebarContext
  /**
   * When we drill down on Article vs Article Candidate in both cases we're drilling into the vlp-style at some ex id, however we need to track which field we drilled into in order to know what title to draw
   */
  xref?: string
  data?: any & { images: Array<any> }
}

export interface ISidebarState {
  left: IIndividualSidebarState
  right: IIndividualSidebarState
}

/**
 * To be serialized the URL
 */
export interface IIndividualSidebarState {
  open?: boolean
  width?: number
  headerExpanded?: boolean

  variant: SidebarVariant
  type: 'modal' | 'sidebar'

  path?: Array<ICatalogItemDetailsPathElement>

  /**
   * The left sidebar has tabs like libraries, filters, etc
   */
  tab?: number | string

  showImageFullscreen?: boolean

  autoFocusDetailItem?: boolean

  sectionState?: { [id: string]: { open?: boolean } }
}

export type SidebarAlign = 'left' | 'right' | 'modal' | 'inline'

const DEFAULT_SIDEBAR_WIDTH = 250

export abstract class AbstractSidebarStore<TabID extends string = ''> extends CatalogInstanceChildStore implements Partial<IIndividualSidebarState> {
  protected constructor(public catalog: CatalogStore, public align: SidebarAlign) {
    super(catalog)

    const {registerEffects} = this

    catalog.isGlobalStore ? synchronizeStores(() => registerEffects()) : registerEffects()
  }

  @observable isDisposed = false

  registerEffects = () => {
    const {stores, catalog, vlp, sidebar, flexLayout, flexLocation, _toDispose, stores: {tab}} = this
    this.everOpen = this.open

    if (flexLocation == null) {
      this._toDispose.push(
        // autorun(() => {
        //   // trace()
        //   const {libraryFilter, open, path, align} = this
        //
        //   libraryFilter && (libraryFilter.showLibraryFilterPopover = null)
        //
        //   if (align == 'right' && open && _.isEmpty(path) && !catalog.showPdf) {
        //     console.trace(`Closing sidebar due to path being empty`, {align, path, open})
        //     this.open = false
        //   }
        // }, {name: `Close sidebar if selected item if path is cleared`}),

        autorun(() => {
          if (this.open && stores.catalogTab?.tab && !_.includes(['catalog'], stores.catalogTab.tab.component) && !flexLayout.knownTabs[stores.catalogTab.tab.component]) {
            this.open = false
          }
        }),

        reaction(() => this.open, () => {
          this.everOpen = this.everOpen || this.open
        }, {name: `Save if sidebar has ever been opened`}),

        // autorun(() => {
        //   if (this.path.length <= 1 && !this.headerExpanded)
        //     this.headerExpanded = true
        // }),
        autorun(() => {
          const {autoFocusDetailItem, catalog: {filter}} = this

          if (autoFocusDetailItem && filter.hasLoaded && this.details?.data && vlp.hierarchy.hasLoadedHierarchy && catalog.index === this.details.index) {
            this.autoFocusDetailItem = false

            runInAction(() => {
              const {details: {data}} = this,
                id = catalog.idFor(data)

              catalog.tryMatchSeasonalHierarchyFor(data)
              catalog.focusedCatalogItemId = id
              catalog.selection.selectedIds.add(id)
              this.open = true
            })
          }
        }))
    }

    if (tab?.id === 'catalogItemDetailsTab' && this.align === 'right') {
      _toDispose.push(
        autorun(() => {
          const {flexLayout: {activeOrLastCatalogRoot: activeCatalogRoot}, stores, catalog: {parseIdFromPath}} = this
          if (activeCatalogRoot) {
            // Track selection as sidebar.right.path
            const {catalog: {focusedCatalogItemStore, selection}} = activeCatalogRoot

            if (focusedCatalogItemStore) {
              this.path = [{where: [{value: focusedCatalogItemStore.id}]}]
              this.open = true
              this.flexLocation = 'catalogItemDetails'
            } else if (selection?.selectedIds?.size) {
              this.path = [{where: [{value: parseIdFromPath(_.first(Array.from(selection.selectedIds.keys())))}]}]
              this.open = true
              this.flexLocation = 'catalogItemDetails'
            }
          }
        }))
    }

    _toDispose.push(
      autorun(() => {
        if (this.flexLocation != null) {
          const {path} = this

          if (path.length) {
            const validPaths = path.filter(p => !p.where || p.where.every(v => v.value != null))
            if (validPaths.length != path.length) {
              DEBUG_SIDEBAR && console.log(`Replacing path due to invalid entries`, toJS({path, validPaths}, {recurseEverything: true}))
              this.path = validPaths
            }
          }

          if (this.selectedPathIndex > this.path.length - 1) {
            this.selectedPathIndex = this.path.length - 1
          }
        }
      }),
      autorun(() => {
        //this.detailsStore.dispose()

        const {stores, catalog:{libraryById}, path} = this,
          pathObserver = _.cloneDeep(path)

        runInAction(() => {
          const {navDetailStores} = this,
            existingDetailStores = navDetailStores.slice()

          // todo pull out _.isEqual into isDirty dict computed
          existingDetailStores.forEach((s, i) => {
            if (i >= path.length) {
              s?.dispose()
              navDetailStores[i] = null
            }
          })

          path.forEach((v, i) => {
            const existing = existingDetailStores[i]

            if (i + 1 > existingDetailStores.length || !_.isEqual(existing?.props?.navItem, v)) {
              try {
                // console.log(`create details store`, stores.storeId, stores.description, v)
                const ds = new CatalogItemSidebarDetailsStore(stores, {navItem: _.cloneDeep(v), context: 'navItem', ...v, library: v.libraryId && libraryById[v.libraryId], align: this.align})
                ds.registerEffects()

                if (!existing) {
                  navDetailStores[i] = ds
                }
                else { // Wait to replace the store we have so that we can show our existing data until we get our new detail pane
                  when(() => ds.hasLoadedData, () => {
                    existing.dispose()
                    navDetailStores[i] = ds
                  })
                }
                // console.log(`Created detail store at ${i} for `, {v, ds})
              } catch (err) {
                console.error(err)
                return null
              }
            }
          })
        })
      })
    )

    this._toDispose = _toDispose.filter(_.identity)
  }

  navDetailStores = observable.array<CatalogItemSidebarDetailsStore>()
  
  _toDispose: Array<IDisposer> = []

  dispose = () => {
    this._toDispose.forEach(f => f && f())
    this._details?.dispose()
    this.isDisposed = true
  }

  @observable lastAction: 'load' | `navigate-${'right' | 'left'}` = 'load'

  @observable.ref catalogLibraries: CatalogLibrariesStore

  @computed get navItems() : Array<DetailsNavItemStore> {
    const {path, stores} = this
    return path.map((item, i) => new DetailsNavItemStore(stores, {index: i, item: _.cloneDeep(item)}))
  }

  @computed get selectedNavStore() {
    const {selectedPathIndex, path, navItems} = this
    return navItems[selectedPathIndex === -1 ? path.length - 1 : selectedPathIndex]
  }

  @observable autoFocusDetailItem = false
  @observable defaultWidth = DEFAULT_SIDEBAR_WIDTH
  @observable initialWidth: number = DEFAULT_SIDEBAR_WIDTH

  whereEqual = (data: any, where: Array<{ field?, value }>) => {
    return !_.isEmpty(where) && where.every(({field, value}) => value === this.es.readField(data, field ?? '_id'))
  }

  @observable.ref _details: CatalogItemSidebarDetailsStore

  get details() {
    const {selectedPathIndex, navDetailStores} = this
    return navDetailStores.length > 0 ? navDetailStores[selectedPathIndex < 0 ? 0 : selectedPathIndex] : this._details
  }

  @observable open = false

  @observable flexLocation: FlexDetailsLocation

  @computed get isModal() {
    return this.align === 'modal'
  }

  @observable width = DEFAULT_SIDEBAR_WIDTH

  @observable libraryView: 'card' | 'list' = 'card'

  /*@action verifyDetailsItemIsInCatalog = () => {
    if(!stores.groupBy?.catalogItemViewStore?.catalogItemIdsArray) return
    const { groupBy: { catalogItemViewStore:{catalogItemIdsArray} }, sidebar:{ right, right: { open } } } = this
    if(open && !_.some(catalogItemIdsArray, (id) => id === right?.detailsStore?.id)) {
        right.closeModal()
        catalog.selection.clear()
    }
  }*/

  @action toggleView = () => this.libraryView = this.libraryView == 'card' ? 'list' : 'card'

  //@observable width = 256
  @observable tab: number = 0
  @action setSelectedTab = id => e => {
    this.tab = _.isNumber(id) ? id : this.tabIndexes.indexOf(id)
    this.overrideSearchTab = true
  }

  @observable scrollToId?: string
  @observable showImageFullscreen = false

  @observable searchText = ''

  @computed get inlineModal() {
    return this.catalog?.catalogConfig?.features?.inlineModal
  }

  @computed get searchWords() {
    return (this.searchText ?? '').trim().split(' ')
  }

  @observable overrideSearchTab = false

  @computed get selectedTab() { return this.tabIndexes[this.tab] }

  get tabIndexes(): Array<TabID | string> {
    const {details} = this
    if (details) {
      const {modal} = details
      if (modal?.config?.tabs) {
        return modal.config.tabs.map(t => t.id)
      }
    }

    return []
  }

  @observable everOpen = false

  @action navGoUp = () => {
    this.selectedPathIndex = Math.max(0, this.selectedPathIndex - 1)
  }

  @observable selectedPathIndex = -1

  set selectedPathItem(v) {
    const lastIndex = this.selectedPathIndex
    runInAction(() => {
      const selectedIndex = this.selectedPathIndex = this.path.indexOf(v)
      this.lastAction = `navigate-${selectedIndex > lastIndex ? 'right' : 'left'}`
    })
  }

  @computed get selectedPathItem(): ICatalogItemDetailsPathElement {
    const {path, selectedPathIndex} = this
    if (selectedPathIndex === -1) { return _.last(path)}

    return path[selectedPathIndex]
  }

  @computed get selectedLibraryDefinition(): LibraryItemConfig {
    const {selectedPathItem, catalog: {_fieldByName, libraryForField}} = this
    let r = selectedPathItem?.libraryId ? this.workspaces.libraryById[selectedPathItem.libraryId] : libraryForField(_fieldByName[selectedPathItem?.catalogField])
    if (r) return r

    // Not everything is a 'library' - fake it
    if (selectedPathItem?.catalogField) {
      const f = _fieldByName[selectedPathItem.catalogField]
      if (f?.esIndex) return {index: f.esIndex, name: f.field}
    }

    return null
  }

  @computed get associatedWorkspace(): WorkspaceConfig {
    const {selectedLibraryDefinition, catalog} = this

    if (selectedLibraryDefinition?.index) {
      return catalog.workspaceByIndex[selectedLibraryDefinition.index]
    }

    return null
  }

  // Taken from ProductStore - might be removable
  @observable action: 'create' | 'duplicate' | 'specification' = null

  @observable path : Array<ICatalogItemDetailsPathElement> = []

  @action selectPathItem = (pathIndex: number, pathItem: ICatalogItemDetailsPathElement) => {
    const {path, stores: {flexLayout: {create: {createItemDetailsTab}}}} = this,
      tabNode = this.tabNode ?? this.stores.catalogTab?.tab

    // console.log(tabNode)

    if (path.length <= pathIndex) {
      //path.push(pathItem)

      if (tabNode) {
        tabNode.openCatalogItemDetailsInNewTab({path: [pathItem], selectedPathIndex: 0})
      }
      else {
        const tab = createItemDetailsTab({path: [pathItem], selectedPathIndex: 0})
        tab.selectInTabSet()
      }
    } else {
      path[pathIndex] = pathItem
    }

    this.selectedPathIndex = pathIndex
  }

  @observable.ref anchorEl: HTMLElement
  @action setAnchorEl = (e: React.MouseEvent) => {
    this.anchorEl = e.target as HTMLElement
  }
  @action clearAnchorEl = () => this.anchorEl = null

  // region Query Params

  @observable sectionState = {}

  @observable headerExpanded = false

  @computed get queryParams(): SidebarQueryParams {
    // Serialize a minified version of our state graph
    const {open, searchText, tab, path, libraryView, showImageFullscreen, autoFocusDetailItem, sectionState, headerExpanded} = this

    return removeUndefinedsNested(
      {
        open: open ? open : undefined,
        // Super expensive to constantly force nav to change // need to debounce this update
        //width: width == DEFAULT_SIDEBAR_WIDTH ? undefined : deployments.deploymentConfig ? width : sidebar.queryParamsParsed ? sidebar.queryParamsParsed[align]?.width : undefined,
        tab,
        path: path.map(p => _.omit(p, ['data'])),
        searchText: searchText == '' ? undefined : searchText,
        libraryView,
        showImageSlider: showImageFullscreen ? true : undefined,
        autoFocusDetailItem: autoFocusDetailItem ? true : undefined,
        sectionState: _.size(sectionState) === 0 ? undefined : sectionState,
        headerExpanded: headerExpanded ? headerExpanded : undefined
      })
  }

  // endregion

  // region State Mutation

  @action confirmNoSaveChanges = () => {
    if (this.details?.isDirty) {
      // if (confirm('Changes will not be saved, continue?')) {
      this.details.cancelMutations()
      return true
      // }
      // return false
    }
    return true
  }

  @action closeModal = () => {
    if (this.align === 'modal') {
      this.open = false
    }
  }

  @action toggleOpen = () => {
    const {stores: {flexLayout, flexLayout: {isMaximized, maximizedTab, toggleKnownTab}}} = this
    if (isMaximized) {
      if (maximizedTab.component === 'inlineCatalogItemDetails') {
        flexLayout.toggleMaximize()
      } else if (!this.open || this.confirmNoSaveChanges()) {
        this.open = !this.open
      }
    } else {
      const {tab, selectedTab, align} = this
      if (align === 'left') {
        toggleKnownTab(tab === 0 ? 'libraries' : tab === 1 ? 'filters' : null)
      }
    }

    const {flexLocation} = this
    if (!isMaximized && flexLocation === 'inlineCatalogItemDetails') {
      this.tabNode?.removeFromParent()
    }
  }

  @observable.ref tabNode: TabNode

  @action openTab = (tab) => {
    if (!this.open) {
      this.open = true
    }
    this.tab = tab
  }

  // endregion

  // region Drill down / Library Selection

  @computed get selectedCatalogFieldLibrary(): ICatalogFieldConfig {
    const {selectedPathItem, catalog: {_fieldByName}} = this
    if (!selectedPathItem) return null

    return selectedPathItem?.catalogField && _fieldByName[selectedPathItem.catalogField]
  }

  @computed get selectedLibrary() {
    const {selectedPathItem, workspaces} = this

    return selectedPathItem?.libraryId ? workspaces.libraryById[selectedPathItem.libraryId] : null
  }

  @computed get selectedLibraryConfig(): LibraryItemConfig {
    if (!this.selectedPathItem) return null

    const {associatedWorkspace, selectedPathItem: {libraryId, catalogField, where, sidebarContext, xref}, align} = this, {workspaces: {workspaceConfig}, catalog, stores: {flexLayoutCatalogRoot}} = this
    if (!libraryId && _.isEmpty(where) && sidebarContext === 'create-catalog-item') {
      return {name: workspaceConfig.itemName, index: catalog.index, editable: true}
    }

    if (!libraryId && _.isEmpty(where)) return null

    if (this.associatedWorkspace) {
      return {name: associatedWorkspace._id, index: associatedWorkspace?.catalog?.index, id: libraryId}
    }

    if (!libraryId && workspaceConfig && !_.isEmpty(where) && align === 'right') {// align right check is for catalog.index is the sidebar detail for right. else it fetch data from catalog.index with some irrelevant id
      return {name: workspaceConfig.itemName, index: catalog.index}
    }

    if (sidebarContext == 'catalog' || !sidebarContext) {
      const field = catalog._fieldByName[libraryId]
      if (field) {
        return this.libraryForField(libraryId, field)
      }
    }

    let r = catalog.libraryById[libraryId]

    return r
  }

  libraryForField = _.memoize((libraryId, field) => ({id: libraryId, name: libraryId, ...field, index: field.esIndex, field: field.esIndexField, filter: null}))

  @computed get sidebarHeaderLabel() {
    const {strings, selectedLibraryConfig, selectedCatalogFieldLibrary: f, catalog} = this
    if (selectedLibraryConfig) {
      return catalog.labelForLibrary({id: selectedLibraryConfig?.id}, 'singular').split('->').map((s, i) => <span className={css.headerPath} key={i}>{s}</span>)
    } else if (f?.field) {
      return catalog.labelForFieldName_currentCatalog(f.field)
    } else {
      return strings.noValue
    }
  }

  @computed get isLibraryItemView() {
    const {selectedPathItem} = this
    return selectedPathItem && selectedPathItem.libraryId && _.isEmpty(selectedPathItem.where)
  }

  get libraryFilter() {
    const {catalog: {filter: {storeByLibraryId}}} = this,
      {selectedLibraryConfig} = this
    return selectedLibraryConfig ? storeByLibraryId.get(selectedLibraryConfig.name) : null
  }

  // @action toggleListView = () => {
  //   nav.updateQueryParams({
  //                           listView: nav.queryParams.listView === 'preview' ? 'info' : 'preview'
  //                         })
  // }
  //
  // @computed get activeSidebarTab() {
  //   let r = parseInt(nav.queryParams.sidebarTab ? nav.queryParams.sidebarTab : '0')
  //   if (r + 1 > this.totalTabs) {
  //     r = 0
  //   }
  //
  //   return r
  // }
  //
  // @action setActiveSidebarTab = (tab) => {
  //   if (tab === null || (this.activeSidebarTab == tab && this.open)) {
  //     this.open = false
  //   } else {
  //     this.open = true
  //     nav.updateQueryParams({sidebarTab: tab, libraryId: undefined, libraryItemId: undefined, listView: undefined, sidebarContext: 'libraries'})
  //   }
  // }

  @action updateState = (state: Partial<IIndividualSidebarState>) => {
    if (!this.open || this.confirmNoSaveChanges()) {
      Object.assign(this, state)
      return true
    }
    return false
  }

  @computed get showBackButton() {
    const {tab, path, selectedLibrary, selectedCatalogFieldLibrary} = this, {workspaces: {visibleLibraries}, flexLayout: {flexEnabled, isMaximized}, catalog: {assignableFields}} = this

    return selectedLibrary != null || selectedCatalogFieldLibrary != null || !_.isEmpty(_.last(path)?.where) //|| ((tab === 0 || (flexEnabled && !isMaximized)) && (visibleLibraries.length + assignableFields.length) > 1)
  }

  @action onGoBack = () => {
    const {path} = this,
      tail = _.last(path)

    if (tail) {
      const {where, libraryId, catalogField, sidebarContext} = tail
      if (path.length > 1) {
        this.path = path.slice(0, path.length - 1)
      } else {
        if (_.isEmpty(where)) {
          tail.libraryId = null
          tail.catalogField = null
        }
        tail.where = null
      }
      this.path =
        path.map(v => {
          if ((v.libraryId && v.libraryId === libraryId) || (v.catalogField && v.catalogField == catalogField)) {
            return tail
          }
          return v
        })
    }
  }

  //endregion

  isShowingSpec({libraryId, where}: { libraryId, where?, libraryItemId? }) {
    const {open, path, tab} = this
    return open && tab === 0 && path.some(p => {
      return p.libraryId == libraryId && _.isEqual(p.where, where)
    })
  }

  @computed get selectedEsIndexes() {
    const {path} = this,
      {catalog: {groupBy: {groupingByName}}} = this

    return _.isEmpty(path) ? null : path.map(({libraryId}) => {
      if (libraryId && groupingByName.has(libraryId)) {
        return groupingByName.get(libraryId).esIndex
      }

      return null
    })
  }

  @observable target = null
  @observable specificationType = null
  @observable alignEl = null
  @observable saveInProgress = false

  @computed get showFilters() { return this.catalog.groupBy.groupByFields.length > 0}

  @action onClose = () => {
    this.open = false

    const {details} = this
    if (details) {
      // Annoying behavior of resetting modal tab every time it's closed/opened
      //detailsStore.searchTabIndex = 0
      // if (detailsStore.modal?.config?.tabs?.length) {
      //   this.tab = detailsStore.modal.config.tabs[0].id
      // }
    }
  }

  // region Libraries
  @action navigateToLibraryIndex = ({esIndex, where}: { esIndex: string, where?: Array<any> }) => {
    this.path = [{esIndex, where}]
  }

  @action navigateToLibrary = ({libraryId, imageId, ...rest}: { libraryId?: string, where?: Array<any>, imageId?: string, name?: string, imageWorkspace?: string }, open = false) => {
    const {workspaces: {libraryById}, catalog, catalog: {_fieldByName, catalogItemName}, flexLayout: {isMaximized}} = this,
      {selectedPathItem, align} = this,
      where = rest.where ?? undefined

    DEBUG_SIDEBAR && DEBUG && console.debug(`Navigating to ${libraryId ? `library ${libraryId}` : `${catalogItemName}`}`, {imageId, rest, open, where})

    let path = isMaximized || align != 'right' ? this.path : _.cloneDeep(this.path)

    if (!libraryId && align === 'right') {
      if (selectedPathItem) {
        const last = _.last(path)
        Object.assign(last, {libraryId: undefined, catalogField: undefined, sidebarContext: undefined, where, imageId})  // Clear where as the library will have it defined
      } else {
        path.push({where, imageId})
      }
    } else if (libraryById[libraryId]) {
      if (isMaximized) {
        this.tab = 0
      }

      if (selectedPathItem) {
        const last = _.last(path)

        Object.assign(last, {libraryId, catalogField: undefined, sidebarContext: 'libraries', where, imageId: undefined})  // Clear where as the library will have it defined
      } else {
        path.push({libraryId, sidebarContext: 'libraries', where, imageId})
      }
    } else if (_fieldByName[libraryId]) {
      this.tab = 0
      if (selectedPathItem) {
        const last = _.last(path)

        Object.assign(last, {libraryId: undefined, catalogField: libraryId, sidebarContext: 'libraries', where, imageId})
      } else {
        path.push({catalogField: libraryId, sidebarContext: 'libraries', where, imageId})
      }
    } else {
      DEBUG_SIDEBAR && console.warn(`Unknown id specified to to navigateToLibrary()`, {libraryId})
    }

    if (align === 'right' && !isMaximized) {
      const {flexLayoutCatalogRoot} = this, {name, imageWorkspace: workspace} = rest
      flexLayoutCatalogRoot.tab.openCatalogItemDetailsInNewTab({
        path,
        name,
        workspace: rest.imageWorkspace ? catalog.index : workspace
      })
      return
    } else {
      if (open) {
        this.open = true
        this.tab = 0
      }
    }
  }

  @action resetWidth = () => {
    this.sidebar.left.width = this.sidebar.left.initialWidth
  }

  // endregion

  @action onKeyDown = (e: React.KeyboardEvent) => {
    const {details, align} = this
    if (e.which == Keys.ESCAPE) {
      if (details) {
        details.searchTabIndex = 0
      }

      this.searchText = ''

      if (this.showImageFullscreen) {
        this.showImageFullscreen = false
      } else if (details?.modal && !site.isShowingPopover && !details.carouselStore.isDragging) {
        const {activeElement} = document
        if (!activeElement.closest('[data-modal]') || activeElement.getAttribute('type') === 'button' || activeElement.hasAttribute('data-modal') || activeElement?.tagName == 'BODY') {
          this.onClose()
        }
      }

      e.preventDefault()
      e.stopPropagation()
    } else if (e.which == Keys.TAB) {
      if (details) {
        const {searchTabIndex, searchResults} = details
        details.searchTabIndex = e.shiftKey
                                 ? searchTabIndex == 0 ? searchResults.length - 1 : searchTabIndex - 1
                                 : searchTabIndex == searchResults.length - 1 ? 0 : searchTabIndex + 1

        e.preventDefault()
        e.stopPropagation()
      }
    }
  }
}

export type LeftTabID = 'libraries' | 'filters' | string

class LeftSidebarStore extends AbstractSidebarStore<LeftTabID> {
  splitterComponent: SplitterLayout
  @action setSplitterComponent = r => this.splitterComponent = r

  constructor(public catalog: CatalogStore) {
    super(catalog, 'left')
    synchronizeStores(() => {
      this._toDispose.push(
        autorun(() => {
          const {width, splitterComponent} = this
          if (splitterComponent && !splitterComponent.state.resizing && width != splitterComponent.state.secondaryPaneSize) {
            splitterComponent.setState({secondaryPaneSize: width})
          }
        }))
    })
  }

  @observable assignToCandidateOnly: boolean = false

  get type(): 'sidebar' | 'modal' {
    return 'sidebar'
  }

  @computed get variant() {
    return this.workspaces.workspaceConfig?.sidebars?.left?.variant ?? 'temporary'
  }

  @computed get hasLibraryView() {
    return this.workspaces.libraryById[this.selectedPathItem?.libraryId] != null
  }

  @computed get hasFieldView() {
    const {catalog: {assignableFields}} = this
    return assignableFields.find(f => f.field === this.selectedPathItem?.catalogField)
  }

  /**
   * Lookup table for sidebar tabs
   */
  get tabIndexes(): Array<LeftTabID> {
    return [this.catalog.shouldRenderLibraryTab ? 'libraries' : null, 'filters'].filter(_.identity)
  }

  @computed get isFiltersShowing() {
    return this.tabIndexes.indexOf('filters') == this.tab && this.open
  }

  @observable width = DEFAULT_SIDEBAR_WIDTH

  @action onSizeChange = width => {
    if (this.width != width) {
      this.width = width
    }
  }

  @action onChangeTab = (e, v) => {
    const {tab, catalog: {flexLayout}} = this

    if (!flexLayout.isMaximized && this.align === 'left') {
      const {flexLayout} = this
      flexLayout.toggleKnownTab(this.tabIndexes[v])
    } else {
      if (tab == v) {
        this.toggleOpen()
      } else {
        this.open = true
        this.tab = v
      }
    }
  }
}

export class RightSidebarStore extends AbstractSidebarStore<string> implements IIndividualSidebarState {
  constructor(public catalog: CatalogStore) {
    super(catalog, 'right')
    synchronizeStores(() => {
      const {catalog, flexLocation} = this

      if (flexLocation != null) return

      this._toDispose.push(
        autorun(() => {
          if (this.open && _.isEmpty(this.path)) {
            this.open = false
          }
        }),
        reaction(() => this.open, () => {
          this.flexLayoutCatalogRoot?.tab?.isMaximized && !this.open && (this.searchText = '')
        }, {name: 'Clear search text on open'})
      )
    })
  }

  @computed get isShowingModal() { return this.isModal && this.open}

  @computed get isShowingLibraryItemsList() {
    const {path} = this
    return path.length === 1 && path[0].sidebarContext === 'libraries' && !path[0].libraryId
  }

  @observable headerExpanded: boolean = false

  @computed get showDetailsView() {
    return this.stores.purpose === 'inline-details' || ((!_.isEmpty(this.selectedPathItem?.where) || this.isCreate) && this.open)
  }

  @computed get isCreate() {
    return this.selectedPathItem?.sidebarContext === 'create-catalog-item'
  }

  @computed get isModal() {
    return this.type === 'modal'
  }

  @computed get type() {
    const {catalog: {settings: {detailLoc}, isShowingClientPDF}, flexLocation} = this
    if (isShowingClientPDF || flexLocation === 'catalogItemDetails') {
      return 'sidebar'
    }

    if (flexLocation === 'inlineCatalogItemDetails') return 'modal'

    return detailLoc ?? 'sidebar'
  }

  @computed get variant(): SidebarVariant {
    return /*this.catalog.settings.detailLoc === 'modal' ? 'modal' :  */ (this.catalog?.catalogConfig?.features?.details?.variant ?? this.workspaces?.workspaceConfig?.sidebars?.right?.variant ?? 'persistent')
  }

  @computed get _data() {
    const data = {exId: 'create-item', attributes: {GNT_MPV_Brand_Enum: 'Draft'}}
    this.path.forEach(p => p.data ? Object.keys(p.data).forEach(k => _.set(data, k, p.data[k])) : null)
    return data
  }

  @computed get displayWidth() {
    // console.log(stores.catalog?.aggregation)
    return this.catalog?.isShowingClientPDF || this.catalog?.aggregation?.isShowingDetailsInSidebar ? 256 : !this.catalog?.aggregation?.isShowingAggregateDetails && this.type == 'modal' ? 0 : this.width
  }
}

export class SidebarStore {
  @observable.ref left: LeftSidebarStore
  @observable.ref right: RightSidebarStore

  toDispose: Array<IDisposer> = []

  constructor(public catalog: CatalogStore) {
    this.left = new LeftSidebarStore(catalog)
    this.right = new RightSidebarStore(catalog)
    synchronizeStores(() => {
    })
  }

  @action dispose = () => {
    this.toDispose.forEach(d => d && d())
    this.toDispose = []

    this.left?.dispose()
    this.right?.dispose()
  }

  setRightSidebarMutationLoader = (isApplyingMutations) => {
    this.right.details && (this.right.details.isApplyingMutations = isApplyingMutations)
  }

  /**
   * Sidebar library item dropped on to a group header or the general catalog to create a new Pending Item
   * @param dragItem
   * @param dropItem
   * @param context
   */
  createCatalogItemFromDragDrop_CreateItems = async (dragItem: IDragDropElement, dropItem: IDragDropElement, context: Partial<DragDropInteraction>) => {
    const {catalog: {createItem, libraryByIndex, _fieldByName, stores: {es}}} = this

    let data = {}

    if (libraryByIndex[dragItem.dragType]) {
      // This is a complex type like a style or colorway being dragged to create a new item

      // Retrieve the full library object
      const val = await es.client.get({index: dragItem.dragType, id: dragItem.id})
      if (!val) {
        throw new Error(`Unable to locate source object ${dragItem.dragType} : ${dragItem.id}`)
      }

      // const catalogField = fields.find(f => f.esIndex === dragItem.dragType)
      // catalogField?.field && _.set(item, catalogField.field, val)
      if (context.field) {
        const fieldDef = _fieldByName[context?.field]

        _.set(data, context.field, val)
        _.isArray(fieldDef?.updateOnAssignment) && fieldDef?.updateOnAssignment?.forEach(f => {
          _.set(data, f, {id: val.id, exId: val.exId, images: val.images})
        })
      }
    }

    let offlineItem: OfflineItemWrapper
    if (dropItem.targetExtra?.store) {
      const store: GroupByGroupStore = dropItem.targetExtra.store
      offlineItem = await store.createGroupCatalogItem(data)
    } else {
      offlineItem = await createItem.createOfflineItem({data})
    }

    // console.log(`sidebar.createFromDrop`, {dragItem: toJS(dragItem), dropItem, offlineItem})
  }

  @computed get queryParams() {
    const {catalog: {isGlobalStore, stores: {flexLayout: {flexEnabled}}}} = this

    if (isGlobalStore && flexEnabled) return {}

    const {left, right} = this
    return this.queryParamsFor({left: left.queryParams, right: right.queryParams})
  }

  queryParamsFor = (params: { left?: SidebarQueryParams, right?: SidebarQueryParams }) => {
    return {
      sidebar: !authStore.userIsAuthenticated ? undefined : Base64.encodeURI(
        stringify(params))
    }
  }

  @computed get serializableState() {
    const {left, right} = this
    return {
      left: left.queryParams,
      right: right.queryParams
    }
  }

  @action assignDifferent = (to: AbstractSidebarStore<any>, from = {}) => {
    to && from && Object.keys(from).forEach(k => {
      if ((!to[k] && from[k]) || !_.isEqual(stringify(to[k]), stringify(from[k]))) {
        DEBUG_SIDEBAR && console.debug(`Assign to ${to.align} - ${k}: ${stringify(to[k])} -> ${stringify(from[k])})`)

        if (!_.has(to, k)) {
          DEBUG_SIDEBAR && console.warn(`Invalid param parsed from query params for sidebar`, {k, from, to})
        } else {
          to[k] = from[k]
        }
      }
    })
  }

  @action deserializeFromSettings = (state: Partial<ISidebarState>) => {
    const {left, right, assignDifferent} = this

    assignDifferent(left, state?.left)
    assignDifferent(right, state?.right)
  }

  /**
   * When the sidebar is an overlay (glassed) we want to disable the toolbars to prevent mouse over effects
   */
  @computed get disableToolbarMenus() {
    return this.catalog.flexLayout.isMaximized && this.right.variant == 'temporary' && this.right.open && !this.catalog.isShowingClientPDF
  }

  @action reset = (initialLoad = false) => {
    DEBUG_SIDEBAR && !initialLoad && DEBUG && console.debug(`${this.catalog.debugId} - reset SidebarStore`)
    const {left, right} = this
    left.tab = Math.min(left.tab, left.tabIndexes.length - 1)
    right.tab = Math.min(right.tab, left.tabIndexes.length - 1)

    left.path = []
    right.path = []
    !initialLoad && nav.updateQueryParams(this.queryParams)
  }

  @computed get styleProperties() {
    const {left, right} = this
    return {
      '--left-sidebar-width': `${left.width}px`,
      '--right-sidebar-width': `${right.displayWidth}px`
    }
  }

  @computed get isShowingModal() {
    return this.modalSidebar != null
  }

  @computed get modalSidebar() {
    const {left, right, catalog: {flexLayout: {isMaximized}}} = this
    return !isMaximized ? null : [left, right].find(s => s.open && s?.type === 'modal')
  }

  @computed get modalViewerItemId() {
    return _.first<ISidebarWhereClause>(this.modalSidebar?.selectedPathItem?.where)?.value
  }
}




// WEBPACK FOOTER //
// ./src/modules/drawer/stores/SidebarStore.tsx